import { partition } from '@seiue/util'
import { RouteConfig } from 'packages/route'

import { BaseNode, TreeNode } from './types'

/**
 * 去除字符串首尾的空格
 *
 * @param str - 字符串
 * @returns 处理后的字符串
 */
export const trimStr = (str?: string) => {
  return str ? str.trim() : str
}

/**
 * 处理 url 参数
 *
 * @param key 需要处理的 key
 * @param str 需要处理的字符串，默认为 location.search
 * @returns string|null
 */
export function getUrlParams(key: string, str?: string) {
  const urlString = str || location.search
  const reg = new RegExp(`(^|&)${key}=([^&]*)(&|$)`)
  const r = urlString.slice(1).match(reg)
  return r ? decodeURIComponent(r[2]) : null
}

type ArrayToTreeOptions<T extends BaseNode> = {
  /**
   * 节点层级，默认从 1 开始
   */
  level?: number
  /**
   * 父节点 ID，默认顶层节点为 0
   */
  parentId?: number
  /**
   * 节点比较函数，默认 weight 从小到大排序
   * 如果 weight 相同则 id 从小到大（后创建的在下方）
   */
  comparator?: (a: T, b: T) => number
}

/**
 * 默认的 arrayToTree 可选参数
 */
const defaultArrayToTreeOptions = {
  level: 1,
  parentId: 0,
  comparator: (a: BaseNode, b: BaseNode) =>
    (a.weight ?? 0) - (b.weight ?? 0) || b.id - a.id,
}

/**
 * 将平铺的树节点的数组回转成树结构
 *
 * @param {T[]} arr - 数组
 * @param {ArrayToTreeOptions} options - 可选参数
 * @returns 树形数据结构节点
 */
export const arrayToTree = <T extends BaseNode>(
  arr: T[],
  options?: ArrayToTreeOptions<T>
): TreeNode<BaseNode<T>>[] => {
  if (!arr?.length) return []

  const { level, parentId, comparator } = {
    ...defaultArrayToTreeOptions,
    ...options,
  }

  const [children, rest] = partition(arr, (a) => a.parentId === parentId)

  return children
    .map((c: T) => ({
      ...c,
      level,
      children: arrayToTree<T>(rest, {
        level: level + 1,
        parentId: c.id,
        comparator,
      }),
    }))
    .sort(comparator)
}

/**
 * 获取路由路径集合
 *
 * @param routes - 路由配置
 * @returns 路由路径
 */
export const getAllRoutesPaths: (routes: RouteConfig[]) => string[] = (
  routes: RouteConfig[]
) => {
  const paths: string[] = []

  routes.forEach((route) => {
    paths.push(route.path)

    if (route.subRoutes) {
      const childPaths = getAllRoutesPaths(route.subRoutes)
      paths.push(...childPaths)
    }
  })

  return paths
}

/**
 * 根据下标获取对应的英文字符表达，最多支持到 ZZ(26 * 26)
 *
 * @param index - 下标
 * @param mode - 返回的数据是大写还是小写，默认'uppercase' ,返回大写
 * @returns 根据下标获取对应的英文字符表达
 */
export const getLetterByIndex = (
  index: number,
  mode: 'lowercase' | 'uppercase' = 'uppercase'
): string => {
  if (index < 0) return ''
  /**
   * 利用 toString 特性获取对应字母
   * (10).toString(36) => a，(35).toString(36) => z
   */
  const letter = (
    mode === 'lowercase'
      ? String.prototype.toLowerCase
      : String.prototype.toUpperCase
  ).call(((index % 26) + 10).toString(36))

  const over26Count = Math.floor(index / 26)

  if (over26Count) {
    return `${getLetterByIndex(over26Count - 1)}${letter}`
  }

  return letter
}

/**
 * 数字精度格式化,默认保留一位小数
 */
export const precisionFormat = (
  origin?: string | number | null,
  precision = 1
) => {
  if (!origin || isNaN(Number(origin))) {
    return origin
  }

  return parseFloat(`${Number(origin).toFixed(1)}`)
}
